import { useLazyQuery, useQuery } from '@apollo/client';
import { Paper, Typography, Box, Grid } from '@mui/material';
import { useSnackbar } from 'notistack';
import React, { useContext, useEffect, useState } from 'react';
import SearchBar from '../components/SearchBar/Search';
import FilterDropDown from '../components/filterDropDown/FilterDropDown';
import Content from '../components/Layout/Content';
import {
  handleTimeFilter,
  searchDataByKey,
  extractValueFromRefDisplayJson,
  formatDateInMMDDYYYYWithComma,
  calculateMedicationEndDate,
  extractNote,
  handleMedicationStatusFilter,
  extractValueFromCoding,
  getJsonInfoFromReference,
  calculateDateFilterTimeStamp,
  getStatusFilterParamList,
} from '../shared/helper';
import { ServiceFlyout } from '../components/ServiceFlyout/ServiceFlyout';
import { SimpleBackdrop } from '../components/SimpleBackdropComponent/SimpleBackdrop';
import ApplicationContext from '../context/applicationContext';
import {
  FETCH_ALL_MEDICATIONS_DISPENSES_QUERY,
  FETCH_ALL_MEDICATIONS_QUERY,
  FETCH_ALL_MEDICATIONS_STATEMENTS_QUERY,
  FETCH_MEDICATION_DISPENSE_BY_ID_QUERY,
  FETCH_MEDICATION_REQUEST_BY_ID_QUERY,
  FETCH_MEDICATION_STATEMENT_BY_ID_QUERY,
} from '../services/MedicationQueries/MedicationsService';
import {
  dateFilterMenuItems,
  DEFAULT_NULL_REPLACEMENT,
  DOSAGE_LIST_ITEMS,
  MEDICATIONS_STATUS_LIST,
  medicationsStatusFilterMenuItems,
  NUMBER_OF_ROWS_PER_PAGE,
} from '../shared/constants';
import {
  formatMedicationsData,
  getIconFromSubItemsArray,
} from '../shared/helper';
import ReusableTable from '../components/ReusableTable/ReusableTable';
import NotFound from '../components/NotFound/NotFoundPage';
import { BreadcrumbComponent } from '../components/BreadcrumbComponent/BreadcrumbComponent';
import {
  MEDICATIONS,
  MEDICATIONS_TABLE_HEADING as tableHeading,
  MEDICATIONS_BREADCRUMBDATA as breadcrumbData,
} from '../shared/Strings';
import PaginationComponent from '../components/paginationComponent/ServerSidePaginationComponent';

const boxContainer = {
  position: 'relative',
  display: 'flex',
  flexWrap: 'wrap',
  top: 40,
  flex: 1,
};

const table = {
  marginTop: 8,
  '& .css-1kn3lhz': {},
};

const DosageList = ({ data: { dosage, notes } }) => {
  const doseLength = dosage?.length;
  return (
    doseLength > 0 && (
      <Paper variant="outlined" sx={{ borderRadius: '8px', p: 3, flex: 1 }}>
        {dosage.map((row, index) => {
          if (!row) {
            return '';
          }
          return (
            <Box
              key={index}
              sx={{ display: 'flex', flexDirection: 'column', mb: 1 }}>
              <Typography variant="navSelected">
                {row.text ??
                  row?.timing?.code?.text ??
                  DEFAULT_NULL_REPLACEMENT}
              </Typography>
              <Grid container sx={{ mt: 2 }}>
                <Grid item xs={4}>
                  <Typography variant="serviceColumnName">
                    {DOSAGE_LIST_ITEMS.NO_OF_PILLS}
                  </Typography>
                  <Typography variant="h5">
                    {row?.timing?.repeat?.frequency ?? DEFAULT_NULL_REPLACEMENT}
                  </Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="serviceColumnName">
                    {DOSAGE_LIST_ITEMS.TIME}
                  </Typography>
                  <Typography variant="h5">
                    {row?.timing?.repeat?.timeOfDay?.[0] ??
                      DEFAULT_NULL_REPLACEMENT}
                  </Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="serviceColumnName">
                    {DOSAGE_LIST_ITEMS.COMMENTS}
                  </Typography>
                  <Typography variant="h5">
                    {notes?.[index]?.text ?? DEFAULT_NULL_REPLACEMENT}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          );
        })}
      </Paper>
    )
  );
};

export const Medications = () => {
  const [openFlyout, setOpenFlyout] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [recordsData, setRecordsData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [timeFilter, setTimeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [modalTitle, setModalTitle] = useState('');
  const [dosageInfo, setDosageInfo] = useState([]);
  const [detailsData, setDetailsData] = useState([]);
  const [selectedTableId, setSelectedTableId] = useState(null);
  const [totalCount, setTotalCount] = useState();
  const [offset, setOffset] = useState(0);
  const { menuItems } = useContext(ApplicationContext);
  const { enqueueSnackbar } = useSnackbar();

  const [fetchAllMedicationsAPI, allMedications] = useLazyQuery(
    FETCH_ALL_MEDICATIONS_QUERY,
    {
      fetchPolicy: 'cache-and-network',
      onError: err => {
        enqueueSnackbar(MEDICATIONS.ON_ERROR_TEXT.ALL_MEDICATIONS, {
          variant: 'error',
        });
      },
    }
  );
  const [fetchMedicationStatementAPI, medicationStatements] = useLazyQuery(
    FETCH_ALL_MEDICATIONS_STATEMENTS_QUERY,
    {
      fetchPolicy: 'cache-and-network',
      onError: err => {
        enqueueSnackbar(MEDICATIONS.ON_ERROR_TEXT.MEDICATION_STATEMENT, {
          variant: 'error',
        });
      },
    }
  );
  const [fetchMedicationDispensesAPI, medicationDispenses] = useLazyQuery(
    FETCH_ALL_MEDICATIONS_DISPENSES_QUERY,
    {
      fetchPolicy: 'cache-and-network',
      onError: err => {
        enqueueSnackbar(MEDICATIONS.ON_ERROR_TEXT.MEDICATION_DISPENSE, {
          variant: 'error',
        });
      },
    }
  );

  const [fetchStatementById, fetchStatementByIdCall] = useLazyQuery(
    FETCH_MEDICATION_STATEMENT_BY_ID_QUERY,
    {
      fetchPolicy: 'cache-and-network',
      onError: err => {
        enqueueSnackbar(MEDICATIONS.ON_ERROR_TEXT.MEDICATION_STATEMENT, {
          variant: 'error',
        });
      },
      onCompleted: res => {
        showFlyout(res.fetchMedicationStatementById);
        //setResult(res.fetchMedicationStatementById);
      },
    }
  );

  const [fetchDispenseById, fetchDispenseByIdCall] = useLazyQuery(
    FETCH_MEDICATION_DISPENSE_BY_ID_QUERY,
    {
      fetchPolicy: 'cache-and-network',
      onError: err => {
        enqueueSnackbar(MEDICATIONS.ON_ERROR_TEXT.MEDICATION_DISPENSE, {
          variant: 'error',
        });
      },
      onCompleted: res => {
        showFlyout(res.fetchMedicationDispenseById);
        //setResult(res.fetchMedicationDispenseById);
      },
    }
  );

  const [fetchRequestById, fetchRequestByIdCall] = useLazyQuery(
    FETCH_MEDICATION_REQUEST_BY_ID_QUERY,
    {
      fetchPolicy: 'cache-and-network',
      onError: err => {
        enqueueSnackbar(MEDICATIONS.ON_ERROR_TEXT.BY_ID, {
          variant: 'error',
        });
      },
      onCompleted: res => {
        showFlyout(res.fetchMedicationById);
        //setResult(res.fetchMedicationById);
      },
    }
  );

  const handleChangeFilter = () => {
    setOffset(0);
  };

  const closeFlyout = () => {
    setOpenFlyout(false);
  };

  const onSearch = text => {
    setSearchValue(text);
  };

  const dateFilterOptions = {
    options: dateFilterMenuItems,
    value: timeFilter,
    changeHandler: ev => {
      setTimeFilter(ev.target.value);
      handleChangeFilter();
    },
  };

  const statusFilterOptions = {
    options: medicationsStatusFilterMenuItems,
    value: statusFilter,
    changeHandler: ev => {
      setStatusFilter(ev.target.value);
      handleChangeFilter();
    },
  };

  const loading =
    allMedications.loading ||
    medicationStatements.loading ||
    medicationDispenses.loading;

  const detailsLoading =
    fetchRequestByIdCall.loading ||
    fetchStatementByIdCall.loading ||
    fetchDispenseByIdCall.loading;

  useEffect(() => {
    if (
      allMedications.data &&
      medicationStatements.data &&
      medicationDispenses.data
    ) {
      let result = [
        allMedications.data.fetchAllMedications.medicationRequestList,
        // ...JSON.parse(medicationStatements.data.fetchMedicationStatement.medicationStatementList),
        // ...JSON.parse(medicationDispenses.data.fetchMedicationDispense.medicationDispenseList),
      ];
      let convertor = JSON.parse(result);
      let data = formatMedicationsData(convertor);
      setTotalCount(allMedications.data?.fetchAllMedications?.count);
      setRecordsData(data);
      setFilteredData(data);
    }
  }, [
    allMedications.data,
    medicationStatements.data,
    medicationDispenses.data,
  ]);

  useEffect(() => {
    const variables = {
      sourceUrl: '',
      count: NUMBER_OF_ROWS_PER_PAGE.toString(),
      offset: offset,
      dateFilter: calculateDateFilterTimeStamp(timeFilter) || 0,
      statusFilter: getStatusFilterParamList(
        statusFilter,
        medicationsStatusFilterMenuItems,
        MEDICATIONS_STATUS_LIST
      ),
      searchFilter: searchValue.trim(),
    };
    fetchAllMedicationsAPI({
      variables,
    });
    fetchMedicationStatementAPI({
      variables,
    });
    fetchMedicationDispensesAPI({
      variables,
    });
  }, [
    offset,
    searchValue,
    timeFilter,
    statusFilter,
    fetchAllMedicationsAPI,
    fetchMedicationStatementAPI,
    fetchMedicationDispensesAPI,
  ]);

  // useEffect(() => {
  //   const timeFilteredData = handleTimeFilter(
  //     timeFilter,
  //     recordsData,
  //     'recordedOn'
  //   );
  //   const statusFilteredData = handleMedicationStatusFilter(
  //     statusFilter,
  //     timeFilteredData,
  //     medicationsStatusFilterMenuItems
  //   );
  //   const searchData = searchDataByKey(
  //     statusFilteredData,
  //     'medication',
  //     searchValue
  //   );
  //   setFilteredData(searchData);
  // }, [timeFilter, statusFilter, searchValue, recordsData]);

  const showFlyout = result => {
    const parseResult = JSON.parse(result?.medicationRequest);
    //console.log(JSON.parse(result?.medicationRequest));

    const medicationRefJsonStr =
      parseResult?.medicationReference?.reference &&
      parseResult?.medicationReference?.display &&
      getJsonInfoFromReference(parseResult.medicationReference.display);

    const tableRowInfo = recordsData.find(obj => obj.id === selectedTableId);
    setDosageInfo({
      dosage: parseResult?.dosageInstruction,
      notes: parseResult?.note,
    });
    const modalData = [
      {
        column_name: MEDICATIONS.MODAL_DATA.STATUS,
        value: tableRowInfo.status,
      },
      {
        column_name: MEDICATIONS.MODAL_DATA.RECORDED_ON,
        value: tableRowInfo.recordedOn,
      },
      {
        column_name: MEDICATIONS.MODAL_DATA.MEDICATION_TYPE,
        value: extractValueFromRefDisplayJson(medicationRefJsonStr, 'form'),
      },
      {
        column_name: MEDICATIONS.MODAL_DATA.DOSAGE,
        value:
          (parseResult?.dosageInstruction?.[0]?.doseAndRate?.[0]
            ?.doseQuantity &&
            `${parseResult?.dosageInstruction[0].doseAndRate[0].doseQuantity.value} ${parseResult?.dosageInstruction[0].doseAndRate[0].doseQuantity.unit}`) ||
          (parseResult?.dosageInstruction?.length &&
            parseResult?.dosageInstruction[0].text) ||
          (parseResult?.dosageInstruction?.length &&
            extractValueFromCoding(parseResult?.dosageInstruction[0].method)) ||
          DEFAULT_NULL_REPLACEMENT,
      },

      {
        column_name: MEDICATIONS.MODAL_DATA.RECORDED_BY,
        value:
          parseResult?.performer?.display ||
          (parseResult?.receiver?.length && parseResult.receiver[0].display) ||
          parseResult?.informationSource?.display ||
          DEFAULT_NULL_REPLACEMENT,
      },
      {
        column_name: MEDICATIONS.MODAL_DATA.PRESCRIBER,
        value:
          parseResult?.recorder?.display ||
          parseResult?.performer?.[0]?.actor?.display ||
          DEFAULT_NULL_REPLACEMENT,
      },
      {
        column_name: MEDICATIONS.MODAL_DATA.CODE,
        value: extractValueFromRefDisplayJson(medicationRefJsonStr, 'code'),
      },
      {
        column_name: MEDICATIONS.MODAL_DATA.START_DATE,
        value:
          parseResult?.dosageInstruction &&
          parseResult?.dosageInstruction.length
            ? parseResult?.dosageInstruction[0].timing &&
              parseResult?.dosageInstruction[0].timing.repeat &&
              parseResult?.dosageInstruction[0].timing.repeat.boundsPeriod
              ? parseResult?.dosageInstruction[0].timing.repeat.boundsPeriod
                  .start
                ? formatDateInMMDDYYYYWithComma(
                    parseResult?.dosageInstruction[0].timing.repeat.boundsPeriod
                      .start
                  )
                : tableRowInfo.recordedOn
              : tableRowInfo.recordedOn
            : tableRowInfo.recordedOn,
      },
      {
        column_name: MEDICATIONS.MODAL_DATA.END_DATE,
        value:
          (parseResult?.authoredOn &&
            parseResult?.dispenseRequest?.quantity?.value &&
            calculateMedicationEndDate(
              parseResult?.authoredOn,
              Number(parseResult?.dispenseRequest.quantity.value)
            )) ||
          DEFAULT_NULL_REPLACEMENT,
      },
      {
        column_name: MEDICATIONS.MODAL_DATA.INGREDIENTS,
        value: extractValueFromRefDisplayJson(
          medicationRefJsonStr,
          'ingredient'
        ),
      },
      {
        column_name: MEDICATIONS.MODAL_DATA.FREQUENCY,
        value:
          parseResult?.dosageInstruction?.[0]?.timing?.repeat?.frequency ||
          DEFAULT_NULL_REPLACEMENT,
      },
      {
        column_name: MEDICATIONS.MODAL_DATA.MANUFACTURE,
        value: extractValueFromRefDisplayJson(
          medicationRefJsonStr,
          'manufacturer'
        ),
      },
      {
        column_name: MEDICATIONS.MODAL_DATA.REQUESTED_BY,
        value:
          (parseResult?.requester && parseResult.requester.display) ||
          (parseResult?.receiver &&
            parseResult.receiver.length &&
            parseResult.receiver[0].display) ||
          (parseResult?.informationSource &&
            parseResult.informationSource.display) ||
          DEFAULT_NULL_REPLACEMENT,
      },
      {
        column_name: MEDICATIONS.MODAL_DATA.NOTE,
        value: extractNote(parseResult?.note),
        width: 12,
      },
    ];
    setDetailsData(modalData);
    setOpenFlyout(true);
  };

  const viewDetailsHandler = id => {
    setSelectedTableId(id);
    const tableRowInfo = recordsData.find(obj => obj.id === id);
    setModalTitle(tableRowInfo.medication);
    if (
      tableRowInfo.medicationType === MEDICATIONS.MEDICATIONS_STATEMENT_TEXT
    ) {
      fetchStatementById({
        variables: {
          medicationStatementId: id,
        },
      });
    } else if (
      tableRowInfo.medicationType === MEDICATIONS.MEDICATIONS_DISPENSE_TEXT
    ) {
      fetchDispenseById({
        variables: {
          medicationDispenseId: id,
        },
      });
    } else {
      fetchRequestById({
        variables: {
          medicationId: id,
        },
      });
    }
  };

  const onPageClick = pageNumber => {
    setOffset((pageNumber - 1) * 10);
  };

  return (
    <Content>
      <BreadcrumbComponent
        breadcrumbData={breadcrumbData}></BreadcrumbComponent>
      <Typography variant="title">{MEDICATIONS.TITLE_TEXT}</Typography>
      <Box sx={boxContainer}>
        <SearchBar
          sx={{
            width: '50%',
            minWidth: '250px',
            maxWidth: '500px',
            marginTop: '8px',
          }}
          onSearch={onSearch}
          placeholder={MEDICATIONS.SEARCH_PLACEHOLDER_TEXT}
          setOffset={setOffset}
        />
        <FilterDropDown {...dateFilterOptions} filterType="Date:" />
        <FilterDropDown {...statusFilterOptions} filterType="Status:" />
      </Box>
      <Box sx={table}>
        {!loading && !filteredData.length ? (
          <NotFound title={MEDICATIONS.TITLE_TEXT} />
        ) : (
          <>
            <ReusableTable
              tableHeading={tableHeading}
              data={filteredData}
              viewDetailsHandler={viewDetailsHandler}
            />
            <div
              style={{
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
                marginTop: 18,
              }}>
              {totalCount > NUMBER_OF_ROWS_PER_PAGE && (
                <PaginationComponent
                  numberOfRowsPerPage={NUMBER_OF_ROWS_PER_PAGE}
                  count={totalCount}
                  onPageClick={onPageClick}
                  page={offset / 10}
                />
              )}
            </div>
          </>
        )}
      </Box>
      <ServiceFlyout
        isOpen={openFlyout}
        handleClose={closeFlyout}
        data={detailsData}
        icon={
          menuItems
            ? getIconFromSubItemsArray(
                menuItems?.Medical?.subItems,
                'Medications'
              )
            : null
        }
        category={MEDICATIONS.TITLE_TEXT}
        title={modalTitle}>
        <DosageList data={dosageInfo} />
      </ServiceFlyout>
      <SimpleBackdrop open={loading || detailsLoading} />
    </Content>
  );
};
